import React, { useState, useEffect, useCallback } from 'react';
import { AdminUserListItem, UserSearchFilters } from '../types';
import { adminService, AdminUsersResponse } from '../services/admin';
import AdminUserDetails from './AdminUserDetails';

interface AdminUsersProps {
  userId: number;
  onNavigateBack: () => void;
}

const AdminUsers: React.FC<AdminUsersProps> = ({ userId, onNavigateBack }) => {
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUserListItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState<'all' | 'trial' | 'active' | 'cancelled' | 'expired' | 'none'>('all');
  const [sortBy, setSortBy] = useState<'email' | 'name' | 'createdAt' | 'subscriptionStatus'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, hasMore: false });

  // Action states
  const [actionUser, setActionUser] = useState<AdminUserListItem | null>(null);
  const [actionType, setActionType] = useState<'grant' | 'grantTrial' | 'revoke' | 'toggleAdmin' | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState<{ success: boolean; message: string } | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: UserSearchFilters = {
        search: searchTerm.trim() || undefined,
        subscriptionStatus: subscriptionFilter,
        sortBy,
        sortOrder,
      };

      const response: AdminUsersResponse = await adminService.getUsers(filters);
      setUsers(response.users);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, subscriptionFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleUserAction = async (action: 'grant' | 'grantTrial' | 'revoke' | 'toggleAdmin') => {
    if (!actionUser) return;

    try {
      setActionLoading(true);
      let result;
      
      if (action === 'grant') {
        result = await adminService.grantFreeAccess(actionUser.id, actionReason || 'Admin decision');
      } else if (action === 'grantTrial') {
        result = await adminService.grantTrial(actionUser.id, actionReason || 'Admin decision');
      } else if (action === 'revoke') {
        result = await adminService.revokeFreeAccess(actionUser.id, actionReason || 'Admin decision');
      } else if (action === 'toggleAdmin') {
        result = await adminService.toggleAdminStatus(actionUser.id, actionReason || 'Admin decision');
      }
      
      if (!result) {
        throw new Error('No result from action');
      }
      
      setActionResult({ success: true, message: result.message });
      
      // Refresh users list to show updated status
      await fetchUsers();
      
      // Close action dialog after a delay
      setTimeout(() => {
        setActionUser(null);
        setActionType(null);
        setActionReason('');
        setActionResult(null);
      }, 2000);
      
    } catch (err) {
      console.error(`Error performing action ${action}:`, err);
      setActionResult({ 
        success: false, 
        message: err instanceof Error ? err.message : `Failed to perform action` 
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (plan: string, status: string) => {
    const baseClass = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    if (plan === 'free' && status === 'none') {
      return <span className={`${baseClass} bg-blue-100 text-blue-800`}>Free Plan</span>;
    }
    
    switch (status) {
      case 'active':
        return <span className={`${baseClass} bg-green-100 text-green-800`}>Paid</span>;
      case 'cancelled':
        return <span className={`${baseClass} bg-yellow-100 text-yellow-800`}>Cancelled</span>;
      case 'expired':
        return <span className={`${baseClass} bg-red-100 text-red-800`}>Expired</span>;
      case 'trial':
        return <span className={`${baseClass} bg-blue-100 text-blue-800`}>3-Month Trial</span>;
      default:
        return <span className={`${baseClass} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  const isUserEligibleForFreeAccess = (user: AdminUserListItem) => {
    return user.plan === 'free' || user.subscriptionStatus !== 'active';
  };

  const isUserOnFreeAccess = (user: AdminUserListItem) => {
    return user.plan === 'professional' && user.subscriptionStatus === 'active' && !user.stripeCustomerId;
  };

  const isUserEligibleForTrial = (user: AdminUserListItem) => {
    return !user.isAdmin && !(user.subscriptionStatus === 'active' && Boolean(user.stripeSubscriptionId));
  };

  // If viewing user details, show the details component
  if (selectedUser) {
    return (
      <AdminUserDetails
        userId={selectedUser.id}
        onNavigateBack={() => setSelectedUser(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onNavigateBack}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="mt-2 text-gray-600">Manage users and grant free access</p>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by email or name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subscription Status</label>
              <select
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="createdAt">Created Date</option>
                <option value="email">Email</option>
                <option value="name">Name</option>
                <option value="subscriptionStatus">Status</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {!loading && (
          <div className="mb-4 text-sm text-gray-600">
            Showing {users.length} of {pagination.total} users
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex space-x-4">
                    <div className="flex-1 space-y-2 py-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
              <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {getStatusBadge(user.plan, user.subscriptionStatus)}
                          {user.subscriptionStatus === 'expired' && user.trialEndsAt && (
                            <span className="text-xs text-gray-500">
                              Trial ended {formatDate(user.trialEndsAt.toString())}
                            </span>
                          )}
                          {isUserOnFreeAccess(user) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              {user.isAdmin ? '🎁 Admin Free Access' : '🎁 Free Access'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.createdAt ? formatDate(user.createdAt.toString()) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.isAdmin ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Admin
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">User</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="text-orange-600 hover:text-orange-900"
                          >
                            View Details
                          </button>
                          {isUserEligibleForFreeAccess(user) && !isUserOnFreeAccess(user) && (
                            <button
                              onClick={() => {
                                setActionUser(user);
                                setActionType('grant');
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              Grant Free Access
                            </button>
                          )}
                          {isUserEligibleForTrial(user) && (
                            <button
                              onClick={() => {
                                setActionUser(user);
                                setActionType('grantTrial');
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Grant 3-Month Trial
                            </button>
                          )}
                          {isUserOnFreeAccess(user) && (
                            <button
                              onClick={() => {
                                setActionUser(user);
                                setActionType('revoke');
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              Revoke Access
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setActionUser(user);
                              setActionType('toggleAdmin');
                            }}
                            className={`${user.isAdmin ? 'text-yellow-600 hover:text-yellow-900' : 'text-blue-600 hover:text-blue-900'}`}
                          >
                            {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Confirmation Modal */}
        {actionUser && actionType && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                  {actionType === 'grant' ? 'Grant Free Access' :
                   actionType === 'grantTrial' ? 'Grant 3-Month Trial' :
                   actionType === 'revoke' ? 'Revoke Free Access' : 
                   actionUser.isAdmin ? 'Remove Admin Status' : 'Grant Admin Status'}
                </h3>
                <p className="text-sm text-gray-500 text-center mb-4">
                  {actionType === 'grantTrial'
                    ? `Give ${actionUser.email} full Professional access for three months?`
                    : actionType === 'grant'
                    ? `Grant unlimited free access to ${actionUser.email}?` 
                    : actionType === 'revoke'
                    ? `Revoke free access from ${actionUser.email}?`
                    : actionUser.isAdmin
                    ? `Remove admin privileges from ${actionUser.email}?`
                    : `Grant admin privileges to ${actionUser.email}?`}
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason (optional)
                  </label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Enter reason for this action..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={3}
                  />
                </div>

                {actionResult && (
                  <div className={`mb-4 p-3 rounded-md ${actionResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {actionResult.message}
                  </div>
                )}

                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => {
                      setActionUser(null);
                      setActionType(null);
                      setActionReason('');
                      setActionResult(null);
                    }}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUserAction(actionType)}
                    disabled={actionLoading}
                    className={`px-4 py-2 rounded-md text-white disabled:opacity-50 ${
                      actionType === 'grantTrial'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : actionType === 'grant'
                        ? 'bg-green-600 hover:bg-green-700' 
                        : actionType === 'revoke'
                        ? 'bg-red-600 hover:bg-red-700'
                        : actionUser.isAdmin
                        ? 'bg-yellow-600 hover:bg-yellow-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {actionLoading ? 'Processing...' :
                     actionType === 'grantTrial' ? 'Start 3-Month Trial' :
                     actionType === 'grant' ? 'Grant Access' : 
                     actionType === 'revoke' ? 'Revoke Access' :
                     actionUser.isAdmin ? 'Remove Admin' : 'Grant Admin'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;